import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAccountingStatus,
  getQBOConnectUrl,
  getXeroConnectUrl,
  disconnectAccounting,
  syncAccounting,
} from '../api/accounting';

export function useAccountingStatus() {
  return useQuery({
    queryKey: ['accounting-status'],
    queryFn: getAccountingStatus,
    // Keep polling while a job is ACTIVE — running (5s, to flip to the final
    // timestamp) or paused/backing-off (20s, to catch the resume). Polling only
    // on `isSyncing` made the button oscillate: a backoff/daily-pause flips
    // isSyncing→false, polling stops, the button reverts to a clickable "Sync
    // now", then a refocus refetch catches the re-claim → "Syncing…" again.
    refetchInterval: (query) => {
      const d = query.state.data;
      if (d?.isSyncing) return 5_000;
      if (d?.paused) return 20_000;
      return false;
    },
  });
}

/**
 * Refresh queries sourced from the synced chart of accounts when a QBO/Xero
 * sync completes. `useAccountingStatus` polls every 5s while syncing; this
 * watches the isSyncing true→false transition and invalidates the dependent
 * queries so e.g. the Payment-adjustments account picker reflects newly-synced
 * accounts without a manual page reload.
 *
 * Invalidating in `useSyncAccounting.onSuccess` would NOT work — the sync runs
 * in the background and finishes after the trigger mutation resolves. The
 * status transition is the correct completion signal.
 */
export function useRefreshOnSyncComplete() {
  const queryClient = useQueryClient();
  const { data: status } = useAccountingStatus();
  const wasSyncing = useRef(false);

  useEffect(() => {
    // A job is "active" while running OR paused (backoff/daily pause). Only fire
    // the refresh on the active→idle transition (job COMPLETED/FAILED/none), not
    // on the transient RUNNING↔paused flips during a backoff cycle.
    const active = !!status?.isSyncing || !!status?.paused;
    if (wasSyncing.current && !active) {
      void queryClient.invalidateQueries({ queryKey: ['orgPaymentAccounts'] });
      void queryClient.invalidateQueries({ queryKey: ['org', 'settings'] });
    }
    wasSyncing.current = active;
  }, [status?.isSyncing, status?.paused, queryClient]);
}

export function useConnectQBO() {
  return useMutation({
    mutationFn: async () => {
      const data = await getQBOConnectUrl();
      window.location.href = data.authUrl;
    },
  });
}

export function useConnectXero() {
  return useMutation({
    mutationFn: async () => {
      const data = await getXeroConnectUrl();
      window.location.href = data.authUrl;
    },
  });
}

export function useDisconnectAccounting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (provider: string) => disconnectAccounting(provider),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounting-status'] });
    },
  });
}

export function useSyncAccounting() {
  const queryClient = useQueryClient();
  return useMutation({
    // `full` forces a complete re-pull ("Re-sync all"); false = incremental.
    mutationFn: (full: boolean) => syncAccounting(full),
    onSuccess: () => {
      setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ['accounting-status'] });
      }, 2000);
    },
  });
}
